import { useEffect, useRef } from 'react';

import { GrayColors, Input, typographyStyle, Button, Colors, Flex, Text } from '@revenuemarket/cds';
import { Typography } from '@revenuemarket/ui';
import { Form, InputRef } from 'antd';
import { SignupEmailVerificationStepModel } from '~components/Auth/signup/steps/SignupEmailVertificationStep';
import { COMMON_YUP } from '~constants/yup-scheme';
import { css } from '~libs/emotion';

// 비밀번호 검증 함수
const validatePassword = (password: string): boolean => {
  if (!password) return false;

  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[^a-zA-Z0-9]/.test(password);

  const typeCount = [hasLetter, hasNumber, hasSpecialChar].filter(Boolean).length;

  // 3종류 이상 + 8자 이상
  if (typeCount >= 3 && password.length >= 8) {
    return true;
  }

  // 2종류 이상 + 10자 이상
  if (typeCount >= 2 && password.length >= 10) {
    return true;
  }

  return false;
};

export interface SignupPasswordStepModel extends SignupEmailVerificationStepModel {
  email: string;
  passcode: string;
}

interface SignupPasswordStepForm {
  password: string;
}

interface Props {
  model: SignupPasswordStepModel;
  onFinish: (values: SignupPasswordStepForm) => void;
}

const SignupPasswordStep = ({ model, onFinish }: Props) => {
  const [form] = Form.useForm<SignupPasswordStepForm>();
  const inputRef = useRef<InputRef | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div
      className={css`
        background: ${Colors.white};
        width: 440px;

        @media (max-width: 768px) {
          max-width: 440px;
          width: 100%;
          align-self: center;
        }
      `}
    >
      <Flex direction="column" gap={12}>
        <div style={{ paddingBottom: 12 }}>
          <Typography
            variant="body1"
            color={GrayColors.gray900}
            style={{
              ...typographyStyle['24_600'],
            }}
          >
            사용하실 비밀번호를 입력해 주세요
          </Typography>
        </div>

        <Form
          form={form}
          onFinish={onFinish}
          initialValues={{ ...model, password: '' }}
          validateTrigger="onChange"
        >
          <Flex direction="column" gap={12}>
            <Form.Item
              name="password"
              style={{ margin: 0 }}
              rules={[
                {
                  validator: async (_, value) => {
                    await COMMON_YUP.password.validate(value);
                  },
                },
              ]}
            >
              <Input inputSize="xlarge" passwordType={true} placeholder="" ref={inputRef} />
            </Form.Item>

            <Text
              textStyle="12_400"
              style={{
                color: GrayColors.gray600,
                marginTop: -4,
              }}
            >
              영문, 숫자, 특수문자 중 2종류 이상을 조합하여 최소 10자리 이상 또는 3종류 이상을
              조합하여 최소 8자리 이상
            </Text>

            <Form.Item noStyle dependencies={['password']}>
              {({ getFieldValue, getFieldError }) => {
                const password = getFieldValue('password');
                const errors = getFieldError('password');
                const isValid = password && validatePassword(password) && errors.length === 0;

                return (
                  <Button
                    buttonType="primary"
                    buttonSize="xlarge"
                    disabled={!isValid}
                    onClick={form.submit}
                  >
                    다음
                  </Button>
                );
              }}
            </Form.Item>
          </Flex>
        </Form>
      </Flex>
    </div>
  );
};

export default SignupPasswordStep;
